<!--
  @component StoragePanel — the Storage surface: a Drive-like file browser
             over the project's real R2 bucket (list / upload / download /
             share-via-signed-URL).
  tier:      screen content (inside a StorageIO mount)
  scope:     one project's object storage. Split out of the old combined
             "Data" panel — Database is now its own section (Phase 2 M:N).
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useStorageIO } from "../../../../global/io/nodes/deployment/StorageIO.svelte";
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import Card from "../../../../global/visuals/primitives/Card.svelte";
  import ConnectionCard from "../../../../global/visuals/primitives/ConnectionCard.svelte";
  import FileBrowser, { type FileEntry } from "../../../../global/visuals/primitives/FileBrowser.svelte";
  import Skeleton from "../../../../global/visuals/primitives/Skeleton.svelte";

  const identity = useIdentityIO();
  const storage = useStorageIO();
  const node = useDeploymentIO();

  const project = $derived(identity.currentProject);

  // ── Storage (REAL R2) ───────────────────────────────────────────
  interface ServerBucket { id: string; status: "provisioning" | "ready" | "failed" | "destroying" | "destroyed"; bucketName: string; keyPrefix: string; }
  interface ServerObject { key: string; fullKey: string; size: number; etag: string; lastModified: string; }
  let buckets = $state<ServerBucket[]>([]);
  let bucketBusy = $state(false);
  const activeBucket = $derived(buckets.find((b) => b.status === "ready" || b.status === "provisioning") ?? null);

  let entries = $state<FileEntry[]>([]);
  let currentPath = $state("/");
  let objectsError = $state<string | null>(null);
  let uploadBusy = $state(false);
  let fileInputEl = $state<HTMLInputElement | null>(null);

  async function refreshBuckets(): Promise<void> {
    if (!project) return;
    try { buckets = (await node.dispatch("storage.list", { target: { id: project.id } })) as ServerBucket[] ?? []; }
    catch { buckets = []; }
  }
  function projectObjects(objs: readonly ServerObject[]): FileEntry[] {
    const out: FileEntry[] = [];
    const folders = new Set<string>();
    for (const o of objs) {
      if (o.key.endsWith("/.keep")) { folders.add("/" + o.key.slice(0, -"/.keep".length)); continue; }
      const seg = o.key.split("/");
      for (let i = 1; i < seg.length; i++) folders.add("/" + seg.slice(0, i).join("/"));
      out.push({ path: "/" + o.key, name: seg[seg.length - 1] ?? o.key, isFolder: false, sizeBytes: o.size, modifiedAtMs: o.lastModified ? new Date(o.lastModified).getTime() : Date.now() });
    }
    for (const f of folders) out.push({ path: f, name: f.split("/").pop() ?? f, isFolder: true, modifiedAtMs: Date.now() });
    return out;
  }
  async function refreshObjects(): Promise<void> {
    if (!project || activeBucket?.status !== "ready") { entries = []; return; }
    try { const r = (await node.dispatch("storage.objects", { target: { id: project.id } })) as { objects: ServerObject[] }; entries = projectObjects(r.objects ?? []); objectsError = null; }
    catch (e) { entries = []; objectsError = e instanceof Error ? e.message : String(e); }
  }
  $effect(() => { if (project) void refreshBuckets(); });
  $effect(() => { if (project && activeBucket?.status === "ready") void refreshObjects(); });

  async function provisionBucket(): Promise<void> {
    if (!project) return;
    bucketBusy = true;
    try { await storage.provisionBucket({ id: project.id }); } catch { /* surfaced below */ }
    finally { bucketBusy = false; void refreshBuckets(); }
  }
  async function handleFiles(ev: Event): Promise<void> {
    if (!project) return;
    const tgt = ev.target as HTMLInputElement;
    const files = tgt.files ? Array.from(tgt.files) : [];
    if (files.length === 0) return;
    uploadBusy = true; objectsError = null;
    try {
      for (const file of files) {
        const folder = currentPath === "/" ? "" : currentPath.replace(/^\//, "") + "/";
        const key = `${folder}${file.name}`;
        const r = (await node.dispatch("storage.share", { target: { id: project.id }, fileRef: key, options: { method: "PUT", expiresInSec: 300 } })) as { url: string };
        const put = await fetch(r.url, { method: "PUT", body: file });
        if (!put.ok) throw new Error(`Upload of ${file.name} failed (${put.status}).`);
      }
      await refreshObjects();
    } catch (e) { objectsError = e instanceof Error ? e.message : String(e); }
    finally { uploadBusy = false; if (tgt) tgt.value = ""; }
  }
  async function openFile(entry: FileEntry): Promise<void> {
    if (!project || entry.isFolder) return;
    try {
      const ref = entry.path.replace(/^\//, "");
      const r = (await node.dispatch("storage.share", { target: { id: project.id }, fileRef: ref, options: { method: "GET", expiresInSec: 3600 } })) as { url: string };
      window.open(r.url, "_blank", "noopener");
    } catch (e) { objectsError = e instanceof Error ? e.message : String(e); }
  }

  const FILE_ACTIONS = [{ id: "open", label: "Open" }];
</script>

<div class="screen">
  <header class="head">
    <h1>Storage</h1>
    <p class="sub">Your app's files — upload media and documents to real object storage your app can read and serve.</p>
  </header>

  <input type="file" multiple hidden bind:this={fileInputEl} onchange={handleFiles} />

  <!-- Files (the Drive — real R2) -->
  <Card elevated eyebrow="Files" title="Object storage">
    {#snippet actions()}
      {#if activeBucket?.status === "ready"}
        <button type="button" class="btn-primary" disabled={uploadBusy} onclick={() => fileInputEl?.click()}>
          {uploadBusy ? "Uploading…" : "Upload"}
        </button>
      {/if}
    {/snippet}

    {#if !activeBucket}
      <div class="setup">
        <p class="setup-title">Set up file storage</p>
        <p class="setup-help">Give your app a place for uploads — images, video, documents. Real object storage, provisioned in seconds.</p>
        <button type="button" class="btn-primary" disabled={bucketBusy} onclick={() => void provisionBucket()}>
          {bucketBusy ? "Setting up…" : "Set up storage"}
        </button>
      </div>
    {:else if activeBucket.status === "provisioning"}
      <div class="provisioning"><Skeleton variant="text" rows={3} /><p class="muted">Setting up your storage…</p></div>
    {:else}
      <div class="browser">
        <FileBrowser {entries} {currentPath} onNavigate={(p) => (currentPath = p)} onFileClick={openFile} onAction={(_, f) => void openFile(f)} actions={FILE_ACTIONS} emptyLabel="No files yet — upload to get started" />
      </div>
      {#if objectsError}<p class="err">{objectsError}</p>{/if}
      <div class="conn-wrap"><ConnectionCard title="How your app reaches storage" fields={[{ label: "Bucket", value: activeBucket.bucketName, mono: true }, { label: "Path prefix", value: activeBucket.keyPrefix, mono: true }]} /></div>
    {/if}
  </Card>
</div>

<style>
  .screen { display: flex; flex-direction: column; gap: var(--space-5); max-width: 980px; font-family: var(--type-font); }
  .head h1 { margin: 0 0 var(--space-2); font-size: 24px; font-weight: 600; letter-spacing: -0.025em; color: var(--text-primary); }
  .sub { margin: 0; font-size: 13.5px; color: var(--text-secondary); line-height: 1.6; max-width: 680px; }
  .muted { margin: var(--space-2) 0 0; font-size: 13px; color: var(--text-tertiary); }
  .err { margin: var(--space-2) 0 0; font-size: 12.5px; color: var(--error); }

  .setup { display: flex; flex-direction: column; gap: var(--space-2); align-items: flex-start; padding: var(--space-2) 0; }
  .setup-title { margin: 0; font-size: 14.5px; font-weight: 600; color: var(--text-primary); }
  .setup-help { margin: 0 0 var(--space-2); font-size: 13px; color: var(--text-secondary); line-height: 1.6; max-width: 560px; }
  .provisioning { display: flex; flex-direction: column; gap: var(--space-2); }

  .browser { min-height: 220px; }
  .conn-wrap { margin-top: var(--space-4); }

  .btn-primary { appearance: none; border-radius: var(--radius-md); padding: 0 var(--space-4); height: 36px; font: inherit; font-size: 13px; font-weight: 500; cursor: pointer; white-space: nowrap; transition: background var(--ease-fast), border-color var(--ease-fast); background: var(--bg-active); color: var(--text-primary); border: 0.5px solid var(--border-strong); }
  .btn-primary:hover:not(:disabled) { background: var(--bg-selected); border-color: var(--border-focus); }
  .btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }
</style>
